import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import LobbyQueueButton from "./LobbyQueueButton";
import LobbyQueueModeSelector from "./LobbyQueueModeSelector";
import EmptyQueueHint from "./EmptyQueueHint";
import QueueEstimateIndicator from "./QueueEstimateIndicator";
import LobbyMatchSetupDeck from "./LobbyMatchSetupDeck";
import {
    LOBBY_GUIDANCE_SLOT_HEIGHT_SX,
    LOBBY_GUIDANCE_SLOT_HEIGHT_MOBILE,
    LOBBY_GUIDANCE_SLOT_HEIGHT_SHORT,
    LOBBY_PARTY_TO_PLAY_GAP_SX,
    LOBBY_PARTY_TO_PLAY_GAP_MOBILE,
    LOBBY_PARTY_TO_PLAY_GAP_SHORT,
    LOBBY_SETUP_DECK_GAP_SX,
    LOBBY_SETUP_DECK_GAP_MOBILE,
    LOBBY_SETUP_DECK_GAP_SHORT,
    MOBILE_NAV_MQ,
    SHORT_VIEWPORT_MQ,
} from "./lobbyPageStyles";

export default function LobbyMatchmakingDock({
    isSearchingView,
    lobbyState,
    matchmakingStarted,
    isLeader,
    hasRequiredQueueSelections,
    currentLobbyCode,
    hasSelectedMaps,
    hasSelectedServerLocations,
    startLobbyMatchmakingByLeader,
    isAccountAllowedToPlay,
    isQueueEffectivelyClosed,
    startSoloMatchmaking,
    showEmptyQueueHint,
    showQueueEstimate,
    queueEstimate,
    discordInviteUrl,
    shouldBlockActions,
    actions,
    lobbyMapsToDisplay,
    lobbyServerLocationsToDisplay,
    serverLocationsRecapSummary,
    lobbyOptionsToDisplay,
    matchmakingRecapSummary,
    canEditLobbySettings,
    platformStats,
    lobbyQueueMode,
    lobbyMemberCount,
    onLobbyQueueModeSelect,
    onOpenSettings,
}) {
    if (isSearchingView) return null;

    return (
        <Box
            className="lobbyMatchmakingDock"
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: LOBBY_SETUP_DECK_GAP_SX,
                pt: LOBBY_PARTY_TO_PLAY_GAP_SX,
                pb: { xs: "1.75rem", xl: "2rem" },
                boxSizing: "border-box",
                [MOBILE_NAV_MQ]: {
                    alignItems: "stretch",
                    gap: LOBBY_SETUP_DECK_GAP_MOBILE,
                    pt: LOBBY_PARTY_TO_PLAY_GAP_MOBILE,
                    pb: "1rem",
                },
                [SHORT_VIEWPORT_MQ]: {
                    gap: LOBBY_SETUP_DECK_GAP_SHORT,
                    pt: LOBBY_PARTY_TO_PLAY_GAP_SHORT,
                },
            }}
        >
            <LobbyQueueModeSelector
                queueMode={lobbyQueueMode}
                isLeader={isLeader}
                disabled={shouldBlockActions?.() || lobbyState === "searching"}
                memberCount={lobbyMemberCount}
                onSelect={onLobbyQueueModeSelect}
            />

            <LobbyQueueButton
                lobbyState={lobbyState}
                matchmakingStarted={matchmakingStarted}
                isLeader={isLeader}
                hasRequiredQueueSelections={hasRequiredQueueSelections}
                currentLobbyCode={currentLobbyCode}
                hasSelectedMaps={hasSelectedMaps}
                hasSelectedServerLocations={hasSelectedServerLocations}
                startLobbyMatchmakingByLeader={startLobbyMatchmakingByLeader}
                isAccountAllowedToPlay={isAccountAllowedToPlay}
                isQueueEffectivelyClosed={isQueueEffectivelyClosed}
                startSoloMatchmaking={startSoloMatchmaking}
            />

            <Box
                className="lobbyQueueGuidanceSlot"
                sx={{
                    width: "100%",
                    height: LOBBY_GUIDANCE_SLOT_HEIGHT_SX,
                    flex: "0 0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: { xs: "0.75rem", xl: "1rem" },
                    boxSizing: "border-box",
                    [MOBILE_NAV_MQ]: {
                        height: LOBBY_GUIDANCE_SLOT_HEIGHT_MOBILE,
                    },
                    [SHORT_VIEWPORT_MQ]: {
                        height: LOBBY_GUIDANCE_SLOT_HEIGHT_SHORT,
                    },
                }}
            >
                {showEmptyQueueHint ? (
                    <EmptyQueueHint discordInviteUrl={discordInviteUrl} />
                ) : showQueueEstimate ? (
                    <QueueEstimateIndicator estimate={queueEstimate} />
                ) : null}
            </Box>

            <LobbyMatchSetupDeck
                shouldBlockActions={shouldBlockActions}
                actions={actions}
                lobbyMapsToDisplay={lobbyMapsToDisplay}
                lobbyServerLocationsToDisplay={lobbyServerLocationsToDisplay}
                serverLocationsRecapSummary={serverLocationsRecapSummary}
                lobbyOptionsToDisplay={lobbyOptionsToDisplay}
                matchmakingRecapSummary={matchmakingRecapSummary}
                canEditLobbySettings={canEditLobbySettings}
                currentLobbyCode={currentLobbyCode}
                platformStats={platformStats}
                onOpenSettings={onOpenSettings}
            />
        </Box>
    );
}

LobbyMatchmakingDock.propTypes = {
    isSearchingView: PropTypes.bool.isRequired,
    lobbyState: PropTypes.string,
    matchmakingStarted: PropTypes.bool,
    isLeader: PropTypes.bool,
    hasRequiredQueueSelections: PropTypes.bool,
    currentLobbyCode: PropTypes.string,
    hasSelectedMaps: PropTypes.bool,
    hasSelectedServerLocations: PropTypes.bool,
    startLobbyMatchmakingByLeader: PropTypes.func.isRequired,
    isAccountAllowedToPlay: PropTypes.bool,
    isQueueEffectivelyClosed: PropTypes.bool,
    startSoloMatchmaking: PropTypes.func.isRequired,
    showEmptyQueueHint: PropTypes.bool,
    showQueueEstimate: PropTypes.bool,
    queueEstimate: PropTypes.object,
    discordInviteUrl: PropTypes.string,
    shouldBlockActions: PropTypes.func.isRequired,
    actions: PropTypes.object.isRequired,
    lobbyMapsToDisplay: PropTypes.array,
    lobbyServerLocationsToDisplay: PropTypes.array,
    serverLocationsRecapSummary: PropTypes.object,
    lobbyOptionsToDisplay: PropTypes.object,
    matchmakingRecapSummary: PropTypes.object,
    canEditLobbySettings: PropTypes.bool,
    platformStats: PropTypes.object,
    lobbyQueueMode: PropTypes.string,
    lobbyMemberCount: PropTypes.number,
    onLobbyQueueModeSelect: PropTypes.func,
    onOpenSettings: PropTypes.func,
};
